import { useState, useCallback } from "react";
import { useAuth } from "./useAuth";
import { GoogleSpreadsheet } from "google-spreadsheet";
import { useAppContext } from "../contexts/app";
import _ from "lodash";

// Global cache for spreadsheet data to prevent rate limiting
const spreadsheetCache = new Map();
const CACHE_TTL_MS = 60000; // 60 seconds cache TTL

// Global cache for GoogleSpreadsheet doc instances (persists across hook instances)
const docCache = new Map();
// Global loading promises to deduplicate concurrent loadInfo() calls
const docLoadingPromises = new Map();
// Global loading promises to deduplicate concurrent getRows() calls
const rowsLoadingPromises = new Map();

const getCacheKey = (spreadsheetId, sheetIndex) => `${spreadsheetId}_${sheetIndex}`;

export const UseGoogleSheet = ({ spreadsheetId, token }) => {
	const { currentUser, logout } = useAuth();
	const [spreadsheet, setSpreadsheet] = useState(null);
	const [sheetRows, setSheetRows] = useState(null);
	const { setRows } = useAppContext();

	const handleAuthError = (error, errorMessage = 'Google Auth Error') => {
		console.warn(errorMessage, error);

		// If the token is expired, navigate to the login page
		if (_.get(error, 'response.status') === 401) {
			console.error(error);
			logout();
		}
	};

	// Get or create a cached spreadsheet document (globally cached)
	const getOrCreateDoc = useCallback(async (accessToken) => {
		// Check if we already have a cached doc for this spreadsheet
		if (docCache.has(spreadsheetId)) {
			const doc = docCache.get(spreadsheetId);
			setSpreadsheet(doc);
			return doc;
		}

		// Check if there's already a loading promise for this spreadsheet
		if (docLoadingPromises.has(spreadsheetId)) {
			const doc = await docLoadingPromises.get(spreadsheetId);
			setSpreadsheet(doc);
			return doc;
		}

		// Create loading promise
		const loadPromise = (async () => {
			const doc = new GoogleSpreadsheet(spreadsheetId, { token: accessToken });
			await doc.loadInfo();
			docCache.set(spreadsheetId, doc);
			return doc;
		})();

		docLoadingPromises.set(spreadsheetId, loadPromise);

		try {
			const doc = await loadPromise;
			setSpreadsheet(doc);
			return doc;
		} finally {
			docLoadingPromises.delete(spreadsheetId);
		}
	}, [spreadsheetId]);

	const loadSheet = async (returnItems, sheetIndex = 0, forceRefresh = false) => {
		const cacheKey = getCacheKey(spreadsheetId, sheetIndex);

		// Check if we have valid cached data
		if (!forceRefresh) {
			const cached = spreadsheetCache.get(cacheKey);
			if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
				setSheetRows(cached.rows);
				setRows(cached.rows.length);
				if (returnItems) {
					return cached.rows;
				}
				return;
			}
		}

		// Prevent duplicate concurrent requests for the same sheet (globally)
		if (rowsLoadingPromises.has(cacheKey)) {
			const rows = await rowsLoadingPromises.get(cacheKey);
			setSheetRows(rows);
			setRows(rows.length);
			if (returnItems) {
				return rows;
			}
			return;
		}

		const loadPromise = (async () => {
			const accessToken = token || currentUser?.credentials?.access_token;
			if (!accessToken) {
				throw new Error('Authentication credentials not available');
			}

			const doc = await getOrCreateDoc(accessToken);
			const sheet = doc.sheetsByIndex[sheetIndex];
			const rows = await sheet.getRows();

			// Cache the result
			spreadsheetCache.set(cacheKey, {
				rows,
				timestamp: Date.now()
			});

			return rows;
		})();

		rowsLoadingPromises.set(cacheKey, loadPromise);

		try {
			const rows = await loadPromise;
			setSheetRows(rows);
			setRows(rows.length);
			if (returnItems) {
				return rows;
			}
		} catch (error) {
			handleAuthError(error, 'Error loading sheet');
			throw error;
		} finally {
			rowsLoadingPromises.delete(cacheKey);
		}
	}

	const loadSheetAsJson = async (returnItems) => {
		try {
			const accessToken = token || currentUser?.credentials?.access_token;
			if (!accessToken) {
				throw new Error('Authentication credentials not available');
			}

			const doc = await getOrCreateDoc(accessToken);
			const sheet = doc.sheetsByIndex[0];
			const rows = await sheet.getRows();
			setRows(rows.length);
			setSheetRows(rows);

			const headerValues = sheet._headerValues;

			if (returnItems) {
				return rows.map(row => {
					let jsonRow = {}
					headerValues.forEach(header => {
						jsonRow[header] = row.get(header);
					});
					return jsonRow;
				});
			}
		} catch (error) {
			handleAuthError(error);
			throw error;
		}
	}

	const addRow = async (row) => {
		try {
			const accessToken = token || currentUser?.credentials?.access_token;
			if (!accessToken) {
				throw new Error('Authentication credentials not available');
			}

			const doc = await getOrCreateDoc(accessToken);
			const sheet = doc.sheetsByIndex[0];
			await sheet.addRow(row);

			// Invalidate cache and reload
			const cacheKey = getCacheKey(spreadsheetId, 0);
			spreadsheetCache.delete(cacheKey);
			await loadSheet(false, 0, true); // Force refresh
		} catch (error) {
			handleAuthError(error);
			throw error;
		}
	}

	const setRow = (row, header, value) => {
		try {
			row.set(header, value);
		} catch (error) {
			handleAuthError(error);
		}
	}

	const saveRow = async (row) => {
		try {
			await row.save();
			// Invalidate cache after saving to ensure fresh data on next load
			const cacheKey = getCacheKey(spreadsheetId, 0);
			spreadsheetCache.delete(cacheKey);
		} catch (error) {
			handleAuthError(error);
		}
	}

	// Invalidate cache for this spreadsheet (useful after external updates)
	const invalidateCache = (sheetIndex = 0) => {
		const cacheKey = getCacheKey(spreadsheetId, sheetIndex);
		spreadsheetCache.delete(cacheKey);
	};

	return { loadSheet, loadSheetAsJson, spreadsheet, sheetRows, setRow, saveRow, addRow, invalidateCache };
};
